import { useState } from 'react';
import { FieldProps } from '@firecms/core';
import { Button, TextField, Typography } from '@firecms/ui';

interface BannerPadding {
    Top?: number;
    Bottom?: number;
    Left?: number;
    Right?: number;
}

interface BannerConfig {
    Container: {
        BackgroundColor: string;
        BackgroundImage?: string;
        BackgroundSize?: string;
        BackgroundPosition?: string;
        BackgroundRepeat?: 'no-repeat' | 'repeat' | 'repeat-x' | 'repeat-y';
        Height?: number;
        Padding?: BannerPadding;
    };
    Image: {
        Src: string;
        Width?: number | string;
        Height?: number | string;
        ObjectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
        Alt?: string;
    };
    Text: {
        Content: string;
        TextType: string;
        Color: string;
        FontFamily?: string;
        TextShadow?: string;
        WebkitTextStroke?: string;
        BackgroundColor?: string;
        BackgroundPadding?: BannerPadding;
    };
    Layout: {
        Direction: 'row' | 'column';
        ImagePosition: 'left' | 'right' | 'top' | 'bottom';
        AlignItems?: string;
        JustifyContent?: string;
        Gap?: number;
    };
    Link?: {
        Url: string;
        OpenInNewTab?: boolean;
    };
}

type BannersMap = Record<string, BannerConfig>;

const EMPTY_BANNER: BannerConfig = {
    Container: { BackgroundColor: '#ffffff' },
    Image: { Src: '' },
    Text: { Content: '', TextType: 'Body1', Color: '#000000' },
    Layout: { Direction: 'row', ImagePosition: 'left' },
};

export function BannersField({ value, setValue }: FieldProps<BannersMap>) {
    const banners = value ?? {};
    const [newKey, setNewKey] = useState('');
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    const sortedKeys = Object.keys(banners).sort((a, b) => {
        if (a === 'default') return -1;
        if (b === 'default') return 1;
        return a.localeCompare(b);
    });

    const addBanner = () => {
        const key = newKey.trim();
        if (!key || banners[key] !== undefined) return;
        setValue({ ...banners, [key]: EMPTY_BANNER });
        setExpanded((e) => ({ ...e, [key]: true }));
        setNewKey('');
    };

    const removeBanner = (key: string) => {
        const next = { ...banners };
        delete next[key];
        setValue(next);
    };

    const updateBanner = (key: string, banner: BannerConfig) => {
        setValue({ ...banners, [key]: banner });
    };

    const toggleExpanded = (key: string) => {
        setExpanded((e) => ({ ...e, [key]: !e[key] }));
    };

    return (
        <div className="flex flex-col gap-3 w-full">
            <Typography variant="caption" className="text-gray-400 dark:text-gray-400">
                Use <code className="bg-gray-100 dark:bg-gray-700 dark:text-gray-200 px-1 rounded">default</code> as the key for the fallback banner shown to all users,
                or enter a vessel ID (ship IMO / vessel ID) to override per vessel.
            </Typography>

            {sortedKeys.map((key) => (
                <div key={key} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    {/* Header row */}
                    <div
                        className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-800 cursor-pointer select-none"
                        onClick={() => toggleExpanded(key)}
                    >
                        <span className="text-sm font-semibold flex-1 text-gray-800 dark:text-gray-100">
                            {key === 'default' ? '⭐ default (fallback)' : `🚢 ${key}`}
                        </span>
                        {banners[key]?.Text?.Content && (
                            <span className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-xs hidden sm:block">
                                {banners[key].Text.Content}
                            </span>
                        )}
                        {key !== 'default' && (
                            <button
                                type="button"
                                className="text-red-400 hover:text-red-600 text-xs px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                                onClick={(e) => { e.stopPropagation(); removeBanner(key); }}
                            >
                                Remove
                            </button>
                        )}
                        <span className="text-gray-400 dark:text-gray-500 text-xs">{expanded[key] ? '▲ collapse' : '▼ edit'}</span>
                    </div>

                    {/* Expanded form */}
                    {expanded[key] && (
                        <div className="p-4 flex flex-col gap-6 bg-white dark:bg-gray-900">
                            <BannerForm
                                banner={banners[key]}
                                onChange={(updated) => updateBanner(key, updated)}
                            />
                        </div>
                    )}
                </div>
            ))}

            {/* Add new banner */}
            <div className="flex gap-2 items-center pt-2 border-t border-gray-100 dark:border-gray-700">
                <TextField
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    placeholder={`default  or  9876543`}
                    size="small"
                    className="flex-1"
                    onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); addBanner(); } }}
                />
                <Button
                    variant="outlined"
                    onClick={addBanner}
                    disabled={!newKey.trim() || banners[newKey.trim()] !== undefined}
                >
                    Add banner
                </Button>
            </div>
        </div>
    );
}

// ─── BannerForm ───────────────────────────────────────────────────────────────

function BannerForm({ banner, onChange }: { banner: BannerConfig; onChange: (b: BannerConfig) => void }) {
    const set = (patch: Partial<BannerConfig>) => onChange({ ...banner, ...patch });

    return (
        <div className="flex flex-col gap-6">
            {/* Container */}
            <Section title="Container">
                <Row label="Background Color">
                    <ColorInput value={banner.Container.BackgroundColor} onChange={(v) => set({ Container: { ...banner.Container, BackgroundColor: v } })} />
                </Row>
                <Row label="Background Image URL">
                    <TextInput value={banner.Container.BackgroundImage} onChange={(v) => set({ Container: { ...banner.Container, BackgroundImage: v } })} placeholder="https://..." />
                </Row>
                <Row label="Background Size">
                    <TextInput value={banner.Container.BackgroundSize} onChange={(v) => set({ Container: { ...banner.Container, BackgroundSize: v } })} placeholder="cover" />
                </Row>
                <Row label="Background Position">
                    <TextInput value={banner.Container.BackgroundPosition} onChange={(v) => set({ Container: { ...banner.Container, BackgroundPosition: v } })} placeholder="center" />
                </Row>
                <Row label="Background Repeat">
                    <SelectInput
                        value={banner.Container.BackgroundRepeat}
                        onChange={(v) => set({ Container: { ...banner.Container, BackgroundRepeat: v as BannerConfig['Container']['BackgroundRepeat'] } })}
                        options={[
                            { value: '', label: '—' },
                            { value: 'no-repeat', label: 'No Repeat' },
                            { value: 'repeat', label: 'Repeat' },
                            { value: 'repeat-x', label: 'Repeat X' },
                            { value: 'repeat-y', label: 'Repeat Y' },
                        ]}
                    />
                </Row>
                <Row label="Height (px)">
                    <NumberInput value={banner.Container.Height} onChange={(v) => set({ Container: { ...banner.Container, Height: v } })} />
                </Row>
                <Row label="Padding">
                    <PaddingInput value={banner.Container.Padding} onChange={(v) => set({ Container: { ...banner.Container, Padding: v } })} />
                </Row>
            </Section>

            {/* Image */}
            <Section title="Image">
                <Row label="Src *">
                    <TextInput value={banner.Image.Src} onChange={(v) => set({ Image: { ...banner.Image, Src: v } })} placeholder="https://..." />
                </Row>
                <Row label="Width">
                    <TextInput value={String(banner.Image.Width ?? '')} onChange={(v) => set({ Image: { ...banner.Image, Width: v } })} placeholder="100% or 200" />
                </Row>
                <Row label="Height">
                    <TextInput value={String(banner.Image.Height ?? '')} onChange={(v) => set({ Image: { ...banner.Image, Height: v } })} placeholder="auto or 120" />
                </Row>
                <Row label="Object Fit">
                    <SelectInput
                        value={banner.Image.ObjectFit}
                        onChange={(v) => set({ Image: { ...banner.Image, ObjectFit: v as BannerConfig['Image']['ObjectFit'] } })}
                        options={[
                            { value: '', label: '—' },
                            { value: 'contain', label: 'Contain' },
                            { value: 'cover', label: 'Cover' },
                            { value: 'fill', label: 'Fill' },
                            { value: 'none', label: 'None' },
                            { value: 'scale-down', label: 'Scale Down' },
                        ]}
                    />
                </Row>
                <Row label="Alt Text">
                    <TextInput value={banner.Image.Alt} onChange={(v) => set({ Image: { ...banner.Image, Alt: v } })} />
                </Row>
            </Section>

            {/* Text */}
            <Section title="Text">
                <Row label="Content *">
                    <textarea
                        value={banner.Text.Content}
                        onChange={(e) => set({ Text: { ...banner.Text, Content: e.target.value } })}
                        rows={2}
                        className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 resize-y bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                </Row>
                <Row label="Text Type *">
                    <SelectInput
                        value={banner.Text.TextType}
                        onChange={(v) => set({ Text: { ...banner.Text, TextType: v } })}
                        options={[
                            { value: 'Body1', label: 'Body 1' },
                            { value: 'Body2', label: 'Body 2' },
                            { value: 'Caption', label: 'Caption' },
                            { value: 'Heading1', label: 'Heading 1' },
                            { value: 'Heading2', label: 'Heading 2' },
                            { value: 'Link', label: 'Link' },
                            { value: 'Overline', label: 'Overline' },
                            { value: 'Subtitle', label: 'Subtitle' },
                        ]}
                    />
                </Row>
                <Row label="Color *">
                    <ColorInput value={banner.Text.Color} onChange={(v) => set({ Text: { ...banner.Text, Color: v } })} />
                </Row>
                <Row label="Font Family">
                    <TextInput value={banner.Text.FontFamily} onChange={(v) => set({ Text: { ...banner.Text, FontFamily: v } })} placeholder="inherit" />
                </Row>
                <Row label="Text Shadow">
                    <TextInput value={banner.Text.TextShadow} onChange={(v) => set({ Text: { ...banner.Text, TextShadow: v } })} placeholder="1px 1px 4px #000" />
                </Row>
                <Row label="Webkit Text Stroke">
                    <TextInput value={banner.Text.WebkitTextStroke} onChange={(v) => set({ Text: { ...banner.Text, WebkitTextStroke: v } })} placeholder="1px black" />
                </Row>
                <Row label="Background Color">
                    <ColorInput value={banner.Text.BackgroundColor} onChange={(v) => set({ Text: { ...banner.Text, BackgroundColor: v } })} />
                </Row>
                <Row label="Background Padding">
                    <PaddingInput value={banner.Text.BackgroundPadding} onChange={(v) => set({ Text: { ...banner.Text, BackgroundPadding: v } })} />
                </Row>
            </Section>

            {/* Layout */}
            <Section title="Layout">
                <Row label="Direction *">
                    <SelectInput
                        value={banner.Layout.Direction}
                        onChange={(v) => set({ Layout: { ...banner.Layout, Direction: v as 'row' | 'column' } })}
                        options={[
                            { value: 'row', label: 'Row' },
                            { value: 'column', label: 'Column' },
                        ]}
                    />
                </Row>
                <Row label="Image Position *">
                    <SelectInput
                        value={banner.Layout.ImagePosition}
                        onChange={(v) => set({ Layout: { ...banner.Layout, ImagePosition: v as 'left' | 'right' | 'top' | 'bottom' } })}
                        options={[
                            { value: 'left', label: 'Left' },
                            { value: 'right', label: 'Right' },
                            { value: 'top', label: 'Top' },
                            { value: 'bottom', label: 'Bottom' },
                        ]}
                    />
                </Row>
                <Row label="Align Items">
                    <SelectInput
                        value={banner.Layout.AlignItems}
                        onChange={(v) => set({ Layout: { ...banner.Layout, AlignItems: v } })}
                        options={[
                            { value: '', label: '—' },
                            { value: 'flex-start', label: 'Start' },
                            { value: 'center', label: 'Center' },
                            { value: 'flex-end', label: 'End' },
                            { value: 'stretch', label: 'Stretch' },
                        ]}
                    />
                </Row>
                <Row label="Justify Content">
                    <SelectInput
                        value={banner.Layout.JustifyContent}
                        onChange={(v) => set({ Layout: { ...banner.Layout, JustifyContent: v } })}
                        options={[
                            { value: '', label: '—' },
                            { value: 'flex-start', label: 'Start' },
                            { value: 'center', label: 'Center' },
                            { value: 'flex-end', label: 'End' },
                            { value: 'space-between', label: 'Space Between' },
                            { value: 'space-around', label: 'Space Around' },
                        ]}
                    />
                </Row>
                <Row label="Gap (px)">
                    <NumberInput value={banner.Layout.Gap} onChange={(v) => set({ Layout: { ...banner.Layout, Gap: v } })} />
                </Row>
            </Section>

            {/* Link */}
            <Section title="Link (optional)">
                <Row label="URL">
                    <TextInput
                        value={banner.Link?.Url}
                        onChange={(v) => set({ Link: v ? { ...banner.Link, Url: v } : undefined })}
                        placeholder="https://..."
                    />
                </Row>
                {banner.Link?.Url && (
                    <Row label="Open in New Tab">
                        <input
                            type="checkbox"
                            checked={banner.Link?.OpenInNewTab ?? false}
                            onChange={(e) => set({ Link: { ...banner.Link!, OpenInNewTab: e.target.checked } })}
                            className="w-4 h-4"
                        />
                    </Row>
                )}
            </Section>
        </div>
    );
}

// ─── Small primitives ─────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-2">
            <Typography variant="label" className="text-gray-500 dark:text-gray-400 uppercase tracking-wide text-xs">{title}</Typography>
            <div className="flex flex-col gap-2 pl-2 border-l-2 border-gray-200 dark:border-gray-700">
                {children}
            </div>
        </div>
    );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex items-start gap-3">
            <span className="text-xs text-gray-500 dark:text-gray-400 w-40 pt-1.5 shrink-0">{label}</span>
            <div className="flex-1">{children}</div>
        </div>
    );
}

const inputClass = "w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-400";

function TextInput({ value, onChange, placeholder }: { value?: string; onChange: (v: string) => void; placeholder?: string }) {
    return (
        <input
            type="text"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={inputClass}
        />
    );
}

function ColorInput({ value, onChange }: { value?: string; onChange: (v: string) => void }) {
    return (
        <div className="flex items-center gap-2">
            <input
                type="color"
                value={value ?? '#ffffff'}
                onChange={(e) => onChange(e.target.value)}
                className="w-8 h-8 rounded border border-gray-300 dark:border-gray-600 cursor-pointer p-0.5 bg-white dark:bg-gray-800"
            />
            <input
                type="text"
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder="#ffffff or rgba(...)"
                className={`flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-400`}
            />
        </div>
    );
}

function NumberInput({ value, onChange }: { value?: number; onChange: (v: number | undefined) => void }) {
    return (
        <input
            type="number"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
            className={inputClass}
        />
    );
}

function SelectInput({ value, onChange, options }: {
    value?: string;
    onChange: (v: string) => void;
    options: { value: string; label: string }[];
}) {
    return (
        <select
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            className={inputClass}
        >
            {options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
            ))}
        </select>
    );
}

function PaddingInput({ value, onChange }: { value?: BannerPadding; onChange: (v: BannerPadding | undefined) => void }) {
    const p = value ?? {};
    const update = (side: keyof BannerPadding, v: number | undefined) => {
        const next = { ...p, [side]: v };
        const isEmpty = Object.values(next).every((x) => x === undefined);
        onChange(isEmpty ? undefined : next);
    };
    return (
        <div className="grid grid-cols-4 gap-1">
            {(['Top', 'Bottom', 'Left', 'Right'] as const).map((side) => (
                <div key={side} className="flex flex-col gap-0.5">
                    <span className="text-xs text-gray-400 dark:text-gray-500 text-center">{side}</span>
                    <input
                        type="number"
                        value={p[side] ?? ''}
                        onChange={(e) => update(side, e.target.value === '' ? undefined : Number(e.target.value))}
                        className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-1 py-1 text-center bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                </div>
            ))}
        </div>
    );
}
